import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, Injector, input, OnInit, output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, FormGroupDirective, NgControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputMaskModule } from 'primeng/inputmask';
import { phoneValidator } from '../../../shared/validators/phone.validator';

@Component({
  selector: 'app-text-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, InputTextModule, InputMaskModule],
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss']
})
export class TextInputComponent implements ControlValueAccessor, OnInit {
  ngControl = inject(NgControl, { optional: true, self: true });

  private readonly formGroupDirective = inject(FormGroupDirective, { optional: true });
  private readonly cdr = inject(ChangeDetectorRef);
  
  // Input properties
  public readonly label = input.required<string>();
  public readonly placeholder = input<string>('');
  public readonly ariaDescribedBy = input<string>('');
  public readonly disabled = input<boolean>(false);
  public readonly readonly = input<boolean>(false);
  public readonly customErrorMessage = input<string>('');
  public readonly fieldName = input<string>('');
  public readonly showRequiredIndicator = input<boolean>(true);
  public readonly inputType = input<'text' | 'email' | 'password' | 'tel' | 'url' | 'phoneNumber'>('text');
  public readonly maxLength = input<number | null>(null);
  public readonly minLength = input<number | null>(null);
  public readonly autocomplete = input<string>('');
  public readonly hint = input<string>('');

  // Output events
  public readonly valueChange = output<string>();
  public readonly blur = output<void>();
  public readonly focus = output<void>();

  // Internal state
  public value = '';
  public isDisabled = false;
  public isTouched = false;
  public isFocused = false;
  public control: AbstractControl | null = null;

  // ControlValueAccessor callbacks
  private onChange = (value: string) => {};
  private onTouched = () => {};

  constructor() {
    // This connects the ControlValueAccessor to the FormControl
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    // Get the control after initialization
    if (this.ngControl?.control) {
      this.control = this.ngControl.control;

      // Add email validator if inputType is 'email'
      if (this.inputType() === 'email') {
        const existingValidators = this.control.validator ? [this.control.validator] : [];
        const validators = [...existingValidators, Validators.email];
        this.control.setValidators(validators);
        this.control.updateValueAndValidity();
      }

      // Add phone validator if inputType is 'phoneNumber' or 'tel'
      if (this.inputType() === 'phoneNumber' || this.inputType() === 'tel') {
        const existingValidators = this.control.validator ? [this.control.validator] : [];
        const validators = [...existingValidators, phoneValidator()];
        this.control.setValidators(validators);
        this.control.updateValueAndValidity();
      }

      // Listen to status changes to detect when control is enabled/disabled
      this.control.statusChanges?.subscribe(() => {
        this.cdr.markForCheck();
      });
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled || this.disabled();
    this.cdr.markForCheck();
  }

  // Event handlers
  public onValueChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
  }

  public onBlur(): void {
    this.isTouched = true;
    this.isFocused = false;
    this.onTouched();
    this.blur.emit();
  }

  public onFocus(): void {
    this.isFocused = true;
    this.focus.emit();
  }

  public onMaskedValueChange(valueOrEvent: any): void {
    // Handle both ngModelChange (passes value directly) and onComplete (passes event object)
    const value = typeof valueOrEvent === 'string' ? valueOrEvent : (valueOrEvent?.value || '');
    this.value = value;
    this.onChange(value);
    this.valueChange.emit(value);
  }

  // Helper getters
  get shouldShowError(): boolean {
    if (!this.control) {
      return false;
    }

    // Don't show errors while user is typing in the field
    if (this.isFocused) {
      return false;
    }

    const isFormSubmitted = this.formGroupDirective?.submitted || false;
    return !!(this.control.invalid && (this.control.dirty || this.control.touched || this.isTouched || isFormSubmitted));
  }

  get errorMessage(): string {
    if (!this.control || !this.control.errors) return '';

    const errors = this.control.errors;
    const fieldName = this.fieldName() || this.label();
    const customMessage = this.customErrorMessage();

    if (customMessage) return customMessage;

    if (errors['required']) {
      return `${fieldName} is required`;
    }

    if (errors['email']) {
      return `Please enter a valid email address`;
    }

    if (errors['pattern']) {
      return `${fieldName} format is invalid`;
    }

    if (errors['minlength']) {
      const requiredLength = errors['minlength'].requiredLength;
      return `${fieldName} must be at least ${requiredLength} characters long`;
    }

    if (errors['maxlength']) {
      const requiredLength = errors['maxlength'].requiredLength;
      return `${fieldName} cannot exceed ${requiredLength} characters`;
    }

    if (errors['min']) {
      return `${fieldName} must be at least ${errors['min'].min}`;
    }

    if (errors['max']) {
      return `${fieldName} cannot exceed ${errors['max'].max}`;
    }

    // Generic error message for other validation errors
    return `${fieldName} is invalid`;
  }

  get isRequired(): boolean {
    if (!this.control || !this.control.validator) return false;
    
    const validator = this.control.validator({} as AbstractControl);
    return validator && validator['required'];
  }

  get inputId(): string {
    return this.ariaDescribedBy() || this.label().toLowerCase().replace(/\s+/g, '-');
  }

  get finalDisabledState(): boolean {
    // Verifica o estado do control diretamente (para capturar mudanças do grupo pai)
    const controlDisabled = this.control?.disabled ?? false;
    return controlDisabled || this.isDisabled || this.disabled();
  }

  get finalReadonlyState(): boolean {
    return this.readonly();
  }
}